import { useMemo, useState } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { VenueMapReportModal } from 'src/modals/VenueMapReportModal';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, Permission, VenueMapReportType } from 'src/WebApiController';

/**
 * Resolves the currently-selected events to their viagogo ids + venue config,
 * and whether they all share a single venue configuration (required to report a
 * map issue in bulk, POS-9306 FR4).
 */
const useBulkReportEventIssueSelection = () => {
  const { getSelection } = useMultiSelectionContext();
  const { data } = useCatalogDataContext();

  const selectedVirtIds = useMemo(
    () => getSelection().groupIds,
    [getSelection]
  );

  return useMemo(() => {
    const events = selectedVirtIds
      .map((virtId) => data?.events?.[virtId]?.event)
      .filter((ev): ev is NonNullable<typeof ev> => !!ev && ev.viagId != null);
    const venueConfigIds = new Set(events.map((e) => e.venCfgId));
    const sameVenueConfig =
      events.length > 0 &&
      venueConfigIds.size === 1 &&
      !venueConfigIds.has(null);

    return {
      eventIds: events.map((e) => e.viagId as number),
      venueConfigId: sameVenueConfig ? events[0].venCfgId : null,
      sameVenueConfig,
    };
  }, [selectedVirtIds, data]);
};

/**
 * "Report event issue" global bulk action (POS-9306 FR4): report an incorrect or
 * missing venue map across all selected events. Disabled unless the selected
 * events share a single venue configuration.
 */
export const ReportEventIssueActions = () => {
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo,
    Permission.Inventory_UpdateListingInfoOnIsPricerOf,
    Permission.Inventory_UpdateListingInfoOnPurchasedBy
  );
  const { areBulkActionsEnabled } = useBulkActionsState();
  const { eventIds, venueConfigId, sameVenueConfig } =
    useBulkReportEventIssueSelection();

  const [isOpen, setIsOpen] = useState(false);
  const [reportType, setReportType] = useState<VenueMapReportType>(
    VenueMapReportType.IncorrectMap
  );

  if (!hasFeature || !hasPermission) {
    return null;
  }

  const openReport = (type: VenueMapReportType) => {
    setReportType(type);
    setIsOpen(true);
  };

  return (
    <>
      <PosDropdown
        key="events-report-issue-actions-dropdown"
        trigger={
          <Button variant="outline">
            <Content id={ContentId.ReportEventIssue} />
            <ChevronDownIcon
              size={vars.iconSize.xs}
              fill={IconsFill.currentColor}
            />
          </Button>
        }
        align="start"
        triggerProps={{ disabled: !areBulkActionsEnabled || !sameVenueConfig }}
      >
        <PosDropdownItem
          onClick={() => openReport(VenueMapReportType.IncorrectMap)}
        >
          <Content id={ContentId.ReportIncorrectMap} />
        </PosDropdownItem>
        <PosDropdownItem
          onClick={() => openReport(VenueMapReportType.MissingMap)}
        >
          <Content id={ContentId.ReportMissingMap} />
        </PosDropdownItem>
      </PosDropdown>
      <VenueMapReportModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        eventIds={eventIds}
        venueConfigId={venueConfigId}
        initialReportType={reportType}
        hideReportTypeToggle
      />
    </>
  );
};
